import { toEthiopian } from './ethiopian';

/** D-14 month names. */
export const ETHIOPIAN_MONTHS_AM = [
  'መስከረም',
  'ጥቅምት',
  'ኅዳር',
  'ታኅሣሥ',
  'ጥር',
  'የካቲት',
  'መጋቢት',
  'ሚያዝያ',
  'ግንቦት',
  'ሰኔ',
  'ሐምሌ',
  'ነሐሴ',
  'ጳጉሜ',
] as const;

export const ETHIOPIAN_MONTHS_EN = [
  'Meskerem',
  'Tikimt',
  'Hidar',
  'Tahsas',
  'Tir',
  'Yekatit',
  'Megabit',
  'Miyazya',
  'Ginbot',
  'Sene',
  'Hamle',
  'Nehase',
  'Pagume',
] as const;

export const GREGORIAN_MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

type Locale = 'am' | 'en' | string;
type Calendar = 'EC' | 'GC' | string;

export function ethiopianMonthName(month: number, locale: Locale): string {
  const list = locale === 'am' ? ETHIOPIAN_MONTHS_AM : ETHIOPIAN_MONTHS_EN;
  return list[month - 1] ?? String(month);
}

/** "2026-10-12" → "12 Oct 2026" */
export function formatGregorian(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${GREGORIAN_MONTHS_SHORT[m! - 1]} ${y}`;
}

/** "2026-10-12" → "ጥቅምት 2, 2019" (am) or "Tikimt 2, 2019 EC" (en). */
export function formatEthiopian(iso: string, locale: Locale): string {
  const e = toEthiopian(iso);
  const base = `${ethiopianMonthName(e.month, locale)} ${e.day}, ${e.year}`;
  return locale === 'am' ? base : `${base} EC`;
}

/** PL-12: primary calendar first, the other in brackets. */
export function formatDualDate(iso: string, locale: Locale, primaryCalendar: Calendar): string {
  return primaryCalendar === 'EC'
    ? `${formatEthiopian(iso, locale)} (${formatGregorian(iso)})`
    : `${formatGregorian(iso)} (${formatEthiopian(iso, locale)})`;
}

/** Compact single-calendar date for chips: "4 Nov" or "ጥቅምት 25". */
export function formatShortDate(iso: string, locale: Locale, primaryCalendar: Calendar): string {
  if (primaryCalendar === 'EC') {
    const e = toEthiopian(iso);
    return `${ethiopianMonthName(e.month, locale)} ${e.day}`;
  }
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${GREGORIAN_MONTHS_SHORT[m! - 1]}`;
}

/** Month label for a Gregorian month with its EC range (RP-2 header). */
export function formatMonthWithEcRange(month: string, locale: Locale): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const first = `${month}-01`;
  const end = `${month}-${String(last).padStart(2, '0')}`;
  return `${GREGORIAN_MONTHS_SHORT[m - 1]} ${y} (${formatEthiopian(first, locale)} – ${formatEthiopian(end, locale)})`;
}
